import React, { useState, useRef, useEffect } from 'react';
import { IconButton } from '@mui/material';
import styled from 'styled-components';
import { useResizedImage } from '../resizeImage';

const KebabButton = styled(IconButton)`
  padding: 4px;
  margin-left: 8px;
  position: relative;
  
  &:hover {
    background-color: #f2f1f380;
  }
`;

const MenuItemIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 8px;
`;

const DropdownMenu = styled.div`
  position: absolute;
  ${props => (props.openUpward ? 'bottom: 100%;' : 'top: 100%;')}
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 180px;
  overflow: hidden;
`;

// Approximate height of one menu row (padding + line-height); used to
// estimate the menu height so we can decide whether to open upward.
const MENU_ITEM_HEIGHT = 37;

const StyledMenuItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 14px;
  font-family: 'IBM Plex Sans', sans-serif;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  white-space: nowrap;
  opacity: ${props => props.disabled ? '0.5' : '1'};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  
  &:hover {
    background-color: ${props => props.disabled ? 'transparent' : '#f2f1f380'};
  }
`;

const MenuContainer = styled.div`
  position: relative;
`;

const ThreeDotsIcon = styled.img`
  transform: rotate(90deg);
  filter: brightness(0);
  opacity: 0.9;
`;

const KebabMenu = ({ 
  menuItems = [], 
  disabled = false,
  size = 'small'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const resizedImages = useResizedImage();
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const handleClick = (event) => {
    event.stopPropagation();
    if (!isOpen) {
      // On open, measure space below the kebab button. If the estimated menu
      // height won't fit beneath it (e.g. last rows above the footer), open
      // upward so the menu isn't clipped (JED-954).
      const rect = buttonRef.current?.getBoundingClientRect();
      const estimatedMenuHeight = menuItems.length * MENU_ITEM_HEIGHT + 2;
      const spaceBelow = rect ? window.innerHeight - rect.bottom : Infinity;
      setOpenUpward(spaceBelow < estimatedMenuHeight);
    }
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleMenuItemClick = (action, event, isDisabled) => {
    event.stopPropagation();
    if (isDisabled) {
      return;
    }
    if (action) {
      action();
    }
    handleClose();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!resizedImages || menuItems.length === 0) {
    return null;
  }

  return (
    <MenuContainer ref={dropdownRef}>
      <KebabButton
        ref={buttonRef}
        onClick={handleClick}
        disabled={disabled}
        size={size}
        aria-controls={isOpen ? 'kebab-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? 'true' : undefined}
      >
        <ThreeDotsIcon src={resizedImages.threeDots} alt="More options" width="16" height="16" />
      </KebabButton>
      {isOpen && (
        <DropdownMenu openUpward={openUpward}>
          {menuItems.map((item, index) => (
            <StyledMenuItem 
              key={index}
              onClick={(event) => handleMenuItemClick(item.action, event, item.disabled)}
              disabled={item.disabled}
            >
              {item.icon && (
                <MenuItemIcon 
                  src={item.icon} 
                  alt={item.label}
                />
              )}
              {item.label}
            </StyledMenuItem>
          ))}
        </DropdownMenu>
      )}
    </MenuContainer>
  );
};

export default KebabMenu;
